import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { 
  PhotoIcon, 
  PencilIcon, 
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { logoSchema, type LogoData } from '../lib/validation';
import { getLogos, createLogo, updateLogo, deleteLogo } from '../lib/supabase';
import type { Logo } from '../types/content';
import DeleteConfirmationModal from './DeleteConfirmationModal';

export default function LogoManagement() {
  const [logos, setLogos] = useState<Logo[]>([]);
  const [selectedLogo, setSelectedLogo] = useState<Logo | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<LogoData>({
    resolver: zodResolver(logoSchema),
    defaultValues: {
      is_active: false
    }
  });

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await getLogos();
      if (fetchError) throw fetchError;
      setLogos(data || []);
    } catch (err) {
      console.error('Error fetching logos:', err);
      setError('Failed to load logos');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: LogoData) => {
    try {
      if (selectedLogo) {
        const { error: updateError } = await updateLogo(selectedLogo.id, data);
        if (updateError) throw updateError;
      } else {
        const { error: createError } = await createLogo(data);
        if (createError) throw createError;
      }
      await fetchLogos();
      reset();
      setSelectedLogo(null);
    } catch (err) {
      console.error('Error saving logo:', err);
      setError('Failed to save logo');
    }
  };

  const handleEdit = (logo: Logo) => {
    setSelectedLogo(logo);
    Object.keys(logo).forEach((key) => {
      setValue(key as keyof LogoData, logo[key as keyof Logo]);
    });
  };

  const handleDelete = (logo: Logo) => {
    setSelectedLogo(logo);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedLogo) return;
    try {
      const { error: deleteError } = await deleteLogo(selectedLogo.id);
      if (deleteError) throw deleteError;
      await fetchLogos();
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError('Failed to delete logo');
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedLogo(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Logo Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
      >
        <h2 className="text-xl font-semibold text-golf-gray mb-6">
          {selectedLogo ? 'Edit Logo' : 'Add New Logo'}
        </h2>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                {...register('name')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                {...register('url')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              {errors.url && (
                <p className="mt-1 text-sm text-red-600">{errors.url.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                {...register('type')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="favicon">Favicon</option>
              </select>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <select
                {...register('location')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              >
                <option value="header">Header</option>
                <option value="footer">Footer</option>
              </select>
              {errors.location && (
                <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Format <span className="text-red-500">*</span>
              </label>
              <select
                {...register('format')}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              >
                <option value="svg">SVG</option>
                <option value="png">PNG</option>
                <option value="ico">ICO</option>
              </select>
              {errors.format && (
                <p className="mt-1 text-sm text-red-600">{errors.format.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Width (px)
              </label>
              <input
                type="number"
                {...register('width', { valueAsNumber: true })}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              {errors.width && (
                <p className="mt-1 text-sm text-red-600">{errors.width.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height (px)
              </label>
              <input
                type="number"
                {...register('height', { valueAsNumber: true })}
                className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
              />
              {errors.height && (
                <p className="mt-1 text-sm text-red-600">{errors.height.message}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('is_active')}
              className="rounded border-gray-300 text-golf-orange focus:ring-golf-orange"
            />
            <label className="text-sm font-medium text-gray-700">
              Set as active
            </label>
          </div>

          <div className="flex justify-end gap-3">
            {selectedLogo && (
              <button
                type="button"
                onClick={() => {
                  reset();
                  setSelectedLogo(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90"
            >
              {selectedLogo ? 'Update Logo' : 'Add Logo'}
            </button>
          </div>
        </form>
      </motion.div>

      {/* Logo List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-golf-gray">Logo Variations</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {logos.length > 0 ? (
            logos.map((logo) => (
              <div
                key={logo.id}
                className="p-4 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {logo.url ? (
                        <img
                          src={logo.url}
                          alt={logo.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <PhotoIcon className="w-8 h-8 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-golf-gray">
                        {logo.name}
                      </h4>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-gray-500 capitalize">
                          {logo.type} • {logo.location}
                        </span>
                        <span className="text-sm text-gray-500 uppercase">
                          {logo.format}
                        </span>
                        {logo.width && logo.height && (
                          <span className="text-sm text-gray-500">
                            {logo.width}x{logo.height}px
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        {logo.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            <CheckCircleIcon className="w-4 h-4" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            <XCircleIcon className="w-4 h-4" />
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(logo)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(logo)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">
              No logos found. Add your first logo above.
            </div>
          )}
        </div>
      </motion.div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title={selectedLogo?.name || ''}
      />
    </div>
  );
}